/**
 * Drawer 'Novo/Editar compromisso' (Dialog largo): Cliente* searchable →
 * Ticket* (carregado após o cliente) → Agendar para* (Atendentes|Agendas) →
 * Data* e Hora* em range → Lembrete* → Descrição*.
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Commitment, CommitmentBody } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Dialog } from '@/components/ui/misc';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import { ticketsListQuery } from '@/features/tickets/tickets-queries';
import { teamListQuery } from '@/features/team/team-queries';
import { calendarsOptions, errorMessage } from './agenda-queries';
import { useCreateCommitment, useUpdateCommitment } from './agenda-mutations';
import { toISODay } from './agenda-format';

type TargetKind = 'user' | 'calendar';

export function CommitmentDrawer({
  initial,
  defaults,
  onClose,
}: {
  initial?: Commitment;
  /** spec 018: "Registrar ação › Compromisso" já vem do atendimento aberto */
  defaults?: { client: ClientRef; ticketNumber: number };
  onClose: () => void;
}) {
  const [client, setClient] = useState<ClientRef | null>(
    initial ? { id: initial.clientId, name: initial.clientName } : (defaults?.client ?? null)
  );
  const [ticketNumber, setTicketNumber] = useState(
    initial ? String(initial.ticketNumber) : defaults ? String(defaults.ticketNumber) : ''
  );
  const [targetKind, setTargetKind] = useState<TargetKind>(
    initial?.calendarId != null ? 'calendar' : 'user'
  );
  const [targetId, setTargetId] = useState(
    initial ? String(initial.calendarId ?? initial.assigneeUserId ?? '') : ''
  );
  const [startDate, setStartDate] = useState(initial?.startDate ?? toISODay(new Date()));
  const [endDate, setEndDate] = useState(initial?.endDate ?? toISODay(new Date()));
  const [startTime, setStartTime] = useState(initial?.startTime ?? '09:00');
  const [endTime, setEndTime] = useState(initial?.endTime ?? '10:00');
  const [reminder, setReminder] = useState(initial ? String(initial.reminderMinutes) : '30');
  const [description, setDescription] = useState(initial?.description ?? '');

  const ticketsQ = useQuery({
    ...ticketsListQuery({ client_ids: client ? [client.id] : [], status: 'all', limit: 50 }),
    enabled: client !== null,
  });
  // a equipe ativa deste tenant (pedido da PoolTrack, 05/10)
  const usersQ = useQuery(teamListQuery());
  const calendarsQ = useQuery(calendarsOptions());
  const activeUsers = usersQ.data?.items ?? [];
  const calendars = calendarsQ.data ?? [];

  const create = useCreateCommitment();
  const update = useUpdateCommitment(initial?.id ?? 0);
  const mutation = initial ? update : create;

  const tickets = ticketsQ.data?.items ?? [];
  // Edição: garante o ticket atual na lista mesmo fora do limite de 50.
  const missingInitialTicket =
    initial !== undefined && !tickets.some((t) => t.ticketNumber === initial.ticketNumber);

  // spec 039: o "Novo compromisso" continua exigindo descrição; o agendamento
  // pela placa nasce sem ela (observação opcional) e se edita aqui sem inventar texto
  const descriptionRequired = initial === undefined || initial.description.trim() !== '';

  const changeClient = (next: ClientRef | null) => {
    setClient(next);
    setTicketNumber('');
  };

  const valid =
    client !== null &&
    ticketNumber !== '' &&
    targetId !== '' &&
    startDate !== '' &&
    endDate !== '' &&
    startDate <= endDate &&
    startTime !== '' &&
    endTime !== '' &&
    reminder !== '' &&
    Number(reminder) >= 0 &&
    (!descriptionRequired || description.trim() !== '');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || client === null) return;
    const body: CommitmentBody = {
      clientId: client.id,
      ticketNumber: Number(ticketNumber),
      assigneeUserId: targetKind === 'user' ? Number(targetId) : null,
      calendarId: targetKind === 'calendar' ? Number(targetId) : null,
      startDate,
      endDate,
      startTime,
      endTime,
      reminderMinutes: Number(reminder),
      description: description.trim(),
    };
    mutation.mutate(body, { onSuccess: onClose });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={initial ? 'Editar compromisso' : 'Novo compromisso'}
      className="sm:max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>
            Cliente <span className="text-destructive">*</span>
          </Label>
          <ClientPicker selected={client} onSelect={changeClient} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="commitment-ticket">
            Ticket <span className="text-destructive">*</span>
          </Label>
          <Select
            id="commitment-ticket"
            value={ticketNumber}
            onChange={(e) => setTicketNumber(e.target.value)}
            disabled={client === null || ticketsQ.isPending}
            required
          >
            <option value="" disabled>
              {client === null
                ? 'Escolha um cliente primeiro…'
                : ticketsQ.isPending
                  ? 'Carregando tickets…'
                  : 'Selecione o ticket…'}
            </option>
            {missingInitialTicket && initial ? (
              <option value={initial.ticketNumber}>
                #{initial.ticketNumber} - {initial.ticketTitle}
              </option>
            ) : null}
            {tickets.map((t) => (
              <option key={t.ticketNumber} value={t.ticketNumber}>
                #{t.ticketNumber} - {t.title}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>
            Agendar para <span className="text-destructive">*</span>
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <Select
              value={targetKind}
              onChange={(e) => {
                setTargetKind(e.target.value as TargetKind);
                setTargetId('');
              }}
              aria-label="Tipo de destino"
            >
              <option value="user">Atendentes</option>
              <option value="calendar">Agendas</option>
            </Select>
            <Select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              aria-label={targetKind === 'user' ? 'Atendente' : 'Agenda'}
              required
            >
              <option value="" disabled>
                {targetKind === 'user' ? 'Selecione o atendente…' : 'Selecione a agenda…'}
              </option>
              {targetKind === 'user'
                ? activeUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))
                : calendars.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>
            Data <span className="text-destructive">*</span>
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              aria-label="Data inicial"
              required
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              aria-label="Data final"
              required
            />
          </div>
          {startDate && endDate && startDate > endDate ? (
            <p className="text-xs text-destructive">
              A data final deve ser igual ou depois da inicial.
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label>
            Hora <span className="text-destructive">*</span>
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              aria-label="Hora inicial"
              required
            />
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              aria-label="Hora final"
              required
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="commitment-reminder">
            Lembrete <span className="text-destructive">*</span>
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="commitment-reminder"
              type="number"
              min={0}
              value={reminder}
              onChange={(e) => setReminder(e.target.value)}
              className="w-24"
              required
            />
            <span className="text-sm text-muted-foreground">Minutos antes</span>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="commitment-description">
            Descrição {descriptionRequired ? <span className="text-destructive">*</span> : null}
          </Label>
          <Textarea
            id="commitment-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required={descriptionRequired}
          />
        </div>

        {mutation.error ? (
          <p className="text-sm text-destructive">{errorMessage(mutation.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={mutation.isPending || !valid}>
            {mutation.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
